import React from 'react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import type { PagesSearchParams } from '../../router';
import { Page as PageLayout, PageHeader, Text, Card, Button, Flex } from '@chatsdet/ui';
import { FileText, Table as TableIcon, TreeStructure } from '@phosphor-icons/react';
import { useAuth } from '../../contexts/AuthContext';
import { useBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import { PagesTableView } from './pages/PagesTableView';
import { SiteGraphTree, useSiteGraph } from '../../features/site-graph';

export function Pages() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { propertyId?: string };
  const propertyId = params.propertyId;

  const searchParams = useSearch({ strict: false }) as PagesSearchParams;
  const view = searchParams.view ?? 'tree';

  const updateSearchParams = React.useCallback(
    (updates: Partial<PagesSearchParams>, resetPage = false) => {
      if (!propertyId) return;
      const next: PagesSearchParams = { ...searchParams, ...updates };
      if (resetPage && !('page' in updates)) {
        next.page = 1;
      }
      const cleaned: PagesSearchParams = {};
      if (next.view && next.view !== 'tree') cleaned.view = next.view;
      if (next.page && next.page !== 1) cleaned.page = next.page;
      if (next.pageSize && next.pageSize !== 25) cleaned.pageSize = next.pageSize;
      if (next.sortBy && next.sortBy !== 'lastSeenAt') cleaned.sortBy = next.sortBy;
      if (next.sortOrder && next.sortOrder !== 'desc') cleaned.sortOrder = next.sortOrder;
      if (next.search) cleaned.search = next.search;
      if (next.qaStatus && next.qaStatus !== 'all') cleaned.qaStatus = next.qaStatus;
      if (next.lastSeenStart) cleaned.lastSeenStart = next.lastSeenStart;
      if (next.lastSeenEnd) cleaned.lastSeenEnd = next.lastSeenEnd;
      if (next.credentialId) cleaned.credentialId = next.credentialId;
      navigate({
        to: '/pages/$propertyId',
        params: { propertyId },
        search: cleaned,
        replace: true,
      });
    },
    [navigate, propertyId, searchParams]
  );

  const { data: siteGraphData } = useSiteGraph(propertyId || undefined);
  const hasRelocationsFeature = !!user?.features?.pageRelocations;

  useBreadcrumbs(
    React.useMemo(
      () => [{ key: 'pages-root', label: 'Pages' }],
      []
    )
  );

  if (!propertyId) {
    return (
      <PageLayout>
        <PageHeader
          icon={<FileText size={24} />}
          title="Pages"
          description="Select a application from the sidebar to view its pages."
        />
        <Card className="p-6 border-dashed border text-center">
          <Text weight="medium">Select an application</Text>
          <Text size="sm" className="text-muted-foreground mt-1">
            Choose an application from the sidebar to view its canonical pages.
          </Text>
        </Card>
      </PageLayout>
    );
  }

  const renderBody = () => {
    if (view === 'tree') {
      return <SiteGraphTree propertyId={propertyId} />;
    }

    return (
      <PagesTableView
        propertyId={propertyId}
        searchParams={searchParams}
        updateSearchParams={updateSearchParams}
        hasRelocationsFeature={hasRelocationsFeature}
      />
    );
  };

  return (
    <PageLayout>
      <Flex justify="between" align="end" className="mb-4">
        <PageHeader
          icon={<FileText size={24} />}
          title="Pages"
          description={
            <Flex align="center" gap="xs" wrap>
              {siteGraphData && (
                <>
                  <Text size="sm" className="text-muted-foreground">
                    {siteGraphData.nodes.length} pages
                  </Text>
                  {siteGraphData.entrypoints.length > 0 && (
                    <>
                      <Text size="sm" className="text-muted-foreground">/</Text>
                      <Text size="sm" className="text-muted-foreground">
                        {siteGraphData.entrypoints.length} {siteGraphData.entrypoints.length === 1 ? 'entrypoint' : 'entrypoints'}
                      </Text>
                    </>
                  )}
                  {siteGraphData.slugPatterns.length > 0 && (
                    <>
                      <Text size="sm" className="text-muted-foreground">/</Text>
                      <Text size="sm" className="text-muted-foreground">
                        {siteGraphData.slugPatterns.length} dynamic
                      </Text>
                    </>
                  )}
                </>
              )}
            </Flex>
          }
        />
        <Flex gap="xs">
          <Button
            variant={view === 'tree' ? 'secondary' : 'ghost'}
            size="sm"
            onPress={() => updateSearchParams({ view: undefined })}
          >
            <TreeStructure size={16} />
            Tree
          </Button>
          <Button
            variant={view === 'table' ? 'secondary' : 'ghost'}
            size="sm"
            onPress={() => updateSearchParams({ view: 'table' })}
          >
            <TableIcon size={16} />
            Table
          </Button>
        </Flex>
      </Flex>
      {renderBody()}
    </PageLayout>
  );
}
